/**
 * Export utilities for graphs
 * Supports PNG, SVG, and PDF exports
 */

import type { Node, Edge } from '@xyflow/react'

export type ExportFormat = 'png' | 'svg' | 'json'

export interface ExportOptions {
  backgroundColor?: string
  quality?: number
  scale?: number
  padding?: number
}

/**
 * Export React Flow graph to PNG
 */
export async function exportToPNG(
  element: HTMLElement,
  options: ExportOptions = {}
): Promise<string> {
  const { toPng } = await import('html-to-image')

  const dataUrl = await toPng(element, {
    backgroundColor: options.backgroundColor || '#ffffff',
    quality: options.quality || 1,
    pixelRatio: options.scale || 2,
  })

  return dataUrl
}

/**
 * Export React Flow graph to SVG
 */
export async function exportToSVG(
  element: HTMLElement,
  options: ExportOptions = {}
): Promise<string> {
  const { toSvg } = await import('html-to-image')

  const dataUrl = await toSvg(element, {
    backgroundColor: options.backgroundColor || '#ffffff',
  })

  return dataUrl
}

/**
 * Download a data URL as a file
 */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.download = filename
  link.href = dataUrl
  link.click()
}

/**
 * Download SVG content as a file
 */
export function downloadSVG(svgContent: string, filename: string): void {
  // If it's a data URL, convert to blob
  if (svgContent.startsWith('data:')) {
    const svgData = svgContent.split(',')[1]
    const decodedSvg = atob(svgData)
    const blob = new Blob([decodedSvg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.download = filename
    link.href = url
    link.click()

    URL.revokeObjectURL(url)
  } else {
    // Raw SVG content
    const blob = new Blob([svgContent], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.download = filename
    link.href = url
    link.click()

    URL.revokeObjectURL(url)
  }
}

/**
 * Export graph data as JSON
 */
export function exportToJSON(
  nodes: Node[],
  edges: Edge[],
  metadata?: Record<string, unknown>
): string {
  return JSON.stringify({
    nodes,
    edges,
    metadata: {
      ...metadata,
      exportedAt: new Date().toISOString(),
      version: '1.0',
    },
  }, null, 2)
}

/**
 * Download text content as a file
 */
export function downloadText(content: string, filename: string, mimeType: string = 'text/plain'): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.download = filename
  link.href = url
  link.click()

  URL.revokeObjectURL(url)
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()

    try {
      document.execCommand('copy')
      return true
    } catch {
      return false
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

/**
 * Generate a unique filename with timestamp
 */
export function generateFilename(prefix: string, extension: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  return `${prefix}-${timestamp}.${extension}`
}
